// components
import {
  ControlledAutoComplete,
  ControlledCheckbox,
  ControlledDatePicker,
  ControlledSelect
} from 'components/shared';
import TextField from 'components/shared/ControlledFields/ControlledTextField';

// hooks
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useGetEmployerOptions } from 'components/admin-portal/Tables/Employer/EmployerCostSettings/components/Sections/General/hooks/useGetEmployerOptions';
import useCreateCostCenterDetailOptions from 'components/admin-portal/Tables/Settings/PlanUnits/hooks//useCreateCostCenterDetailOptions';
import { useGetCostCenterDetails } from 'components/admin-portal/Tables/Settings/PlanUnits/hooks//useGetCostCenterDetails';

// types
import { CostCenterDetailFlag } from '../../../types';
import {
  ParentCostCenterFieldProps,
  SubEntityFieldsProps
} from './SubEntityFields.types';

// utils
import { orderBy } from 'lodash';
import { filterOptions } from '../utils/utils';

export const CodeField: React.FC<SubEntityFieldsProps> = ({
  name = 'code'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.code')}
    />
  );
};

export const NameField: React.FC<SubEntityFieldsProps> = ({
  name = 'name'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.name')}
    />
  );
};

export const NameShortField: React.FC<SubEntityFieldsProps> = ({
  name = 'nameShort'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.shortName')}
    />
  );
};

export const CodeExtField: React.FC<SubEntityFieldsProps> = ({
  name = 'codeExt'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.exportCode')}
    />
  );
};

export const CodeExtSecondaryField: React.FC<SubEntityFieldsProps> = ({
  name = 'codeExtSecondary'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.exportCode2')}
    />
  );
};

export const CodeImpField: React.FC<SubEntityFieldsProps> = ({
  name = 'codeImp'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importCode')}
    />
  );
};

export const CodeImpSecondaryField: React.FC<SubEntityFieldsProps> = ({
  name = 'codeImpSecondary'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <TextField
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importCode2')}
    />
  );
};

export const BenefitStartDateField: React.FC<SubEntityFieldsProps> = ({
  name = 'benefitStartDate'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('costCenterDetail.importStartDate')}
      fullWidth={true}
    />
  );
};

export const FinancialCostCenterField: React.FC<SubEntityFieldsProps> = ({
  name = 'finBrDynId',
  setFinancialCostCenterFieldOptions
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const { data: costCenterDetails } = useGetCostCenterDetails();
  const costCenterDetailOptions =
    useCreateCostCenterDetailOptions(costCenterDetails);

  useEffect(() => {
    setFinancialCostCenterFieldOptions?.(costCenterDetailOptions);
  }, [costCenterDetails]);

  return (
    <ControlledAutoComplete
      control={control}
      variant="outlined"
      label={t('costCenterDetail.financialCostCenter')}
      name={name}
      options={orderBy(
        costCenterDetailOptions,
        ['validFrom', 'label'],
        ['desc', 'asc']
      )}
      idProperty="value"
      optionLabelProp="label"
      filterOptions={filterOptions}
      disableSorting
      fullWidth
    />
  );
};

export const ParentCostCenterField: React.FC<ParentCostCenterFieldProps> = ({
  name = 'parentBrDynId',
  selectedBrDynId,
  setParentCostCenterOptions
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const { data: costCenterDetails } = useGetCostCenterDetails();
  const costCenterDetailOptions =
    useCreateCostCenterDetailOptions(costCenterDetails);

  // remove the current selectedBrDynId from the options
  const filteredCostCenterDetailOptions = costCenterDetailOptions.filter(
    (option) => option.value !== selectedBrDynId
  );

  useEffect(() => {
    setParentCostCenterOptions?.(costCenterDetailOptions);
  }, [costCenterDetails]);

  return (
    <ControlledAutoComplete
      control={control}
      variant="outlined"
      label={t('costCenterDetail.parentCostCenter')}
      name={name}
      options={orderBy(
        selectedBrDynId
          ? filteredCostCenterDetailOptions
          : costCenterDetailOptions,
        ['validFrom', 'label'],
        ['desc', 'asc']
      )}
      idProperty="value"
      optionLabelProp="label"
      disableSorting
      fullWidth
      filterOptions={filterOptions}
    />
  );
};

export const BUHBlockedField: React.FC<SubEntityFieldsProps> = ({
  name = `flags[${CostCenterDetailFlag.BUH_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.noBUH')}
    />
  );
};

export const PlanningExportField: React.FC<SubEntityFieldsProps> = ({
  name = `flags[${CostCenterDetailFlag.PLANNING_EXPORT}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.excludeFromExport')}
    />
  );
};

export const VATDeductibleField: React.FC<SubEntityFieldsProps> = ({
  name = `flags[${CostCenterDetailFlag.VAT_DEDUCTIBLE}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.VATDeductible')}
    />
  );
};

export const InvoiceDebitBlockedField: React.FC<SubEntityFieldsProps> = ({
  name = `flags[${CostCenterDetailFlag.INVOICE_DEBIT_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.excludeBorrowFromInvoice')}
    />
  );
};

export const InvoiceCreditBlockedField: React.FC<SubEntityFieldsProps> = ({
  name = `flags[${CostCenterDetailFlag.INVOICE_CREDIT_BLOCKED}]`
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledCheckbox
      control={control}
      name={name}
      label={t('costCenterDetail.excludeLendFromInvoice')}
    />
  );
};

export const ValidFromField: React.FC<SubEntityFieldsProps> = ({
  name = 'valid.from'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('validFrom')}
      fullWidth={true}
    />
  );
};

export const ValidToField: React.FC<SubEntityFieldsProps> = ({
  name = 'valid.to'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();

  return (
    <ControlledDatePicker
      control={control}
      name={name}
      variant="outlined"
      label={t('validTo')}
      fullWidth={true}
    />
  );
};

export const EmployerField: React.FC<SubEntityFieldsProps> = ({
  name = 'employerId'
}) => {
  const { t } = useTranslation('common');
  const { control } = useFormContext();
  const employerOptions = useGetEmployerOptions();

  return (
    <ControlledSelect
      control={control}
      variant="outlined"
      label={t('costCenterDetail.employer')}
      name={name}
      options={employerOptions}
    />
  );
};
